import React, { useState, useEffect } from "react";
import { getEditorPaymentReport } from "../lib/api";
import { toast } from "sonner";
import { CurrencyDollar, User, VideoCamera, ArrowDown } from "@phosphor-icons/react";

export default function EditorReport() {
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEditor, setExpandedEditor] = useState(null);

  useEffect(() => {
    async function fetchReport() {
      try {
        const res = await getEditorPaymentReport();
        setReport(res.data || []);
      } catch {
        toast.error("Failed to load editor report");
      } finally {
        setLoading(false);
      }
    }
    fetchReport();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-64" />
        <div className="skeleton h-64 w-full" />
      </div>
    );
  }

  const totalPayment = report.reduce((sum, r) => sum + (r.total_payment || 0), 0);
  const totalCompleted = report.reduce((sum, r) => sum + (r.completed || 0), 0);

  return (
    <div className="space-y-6" data-testid="editor-report-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Editor Payment Report</h1>
        <p className="text-gray-400 text-sm mt-1">Track editor performance and payments</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="surface-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/15 flex items-center justify-center">
              <User size={20} className="text-cyan-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{report.length}</p>
              <p className="text-xs text-gray-400">Total Editors</p>
            </div>
          </div>
        </div>
        <div className="surface-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <VideoCamera size={20} className="text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{totalCompleted}</p>
              <p className="text-xs text-gray-400">Videos Completed</p>
            </div>
          </div>
        </div>
        <div className="surface-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <CurrencyDollar size={20} className="text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">${totalPayment.toLocaleString()}</p>
              <p className="text-xs text-gray-400">Total Payment Due</p>
            </div>
          </div>
        </div>
      </div>

      {/* Editor Table */}
      <div className="surface-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#0B0C10] text-[#A0AEC0] text-sm uppercase tracking-wider">
                <th className="text-left p-4 font-medium">Editor</th>
                <th className="text-left p-4 font-medium">Rate/Video</th>
                <th className="text-left p-4 font-medium">Assigned</th>
                <th className="text-left p-4 font-medium">In Progress</th>
                <th className="text-left p-4 font-medium">Completed</th>
                <th className="text-left p-4 font-medium">Total Payment</th>
                <th className="text-left p-4 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {report.map((r) => (
                <React.Fragment key={r.editor_id}>
                  <tr className="table-row" data-testid={`editor-report-row-${r.editor_id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: r.editor_color }} />
                        <span className="text-white font-medium text-sm">{r.editor_name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-gray-300 text-sm">${r.payment_per_video}</td>
                    <td className="p-4 text-gray-300 text-sm">{r.total_assigned}</td>
                    <td className="p-4 text-cyan-400 text-sm font-medium">{r.in_progress}</td>
                    <td className="p-4 text-emerald-400 text-sm font-medium">{r.completed}</td>
                    <td className="p-4 text-amber-400 text-sm font-bold">${r.total_payment.toLocaleString()}</td>
                    <td className="p-4">
                      <button
                        onClick={() => setExpandedEditor(expandedEditor === r.editor_id ? null : r.editor_id)}
                        className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white"
                        data-testid={`expand-editor-${r.editor_id}`}
                      >
                        <ArrowDown size={16} className={`transition-transform ${expandedEditor === r.editor_id ? "rotate-180" : ""}`} />
                      </button>
                    </td>
                  </tr>
                  {expandedEditor === r.editor_id && (
                    <tr>
                      <td colSpan={7} className="p-0">
                        <div className="bg-white/5 p-4 border-t border-b border-white/5">
                          <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Videos assigned to {r.editor_name}</p>
                          <div className="space-y-2">
                            {r.videos.length === 0 ? (
                              <p className="text-gray-500 text-sm">No videos assigned</p>
                            ) : (
                              r.videos.map((v) => (
                                <div key={v.id} className="flex items-center justify-between text-sm">
                                  <span className="text-white">{v.title}</span>
                                  <div className="flex items-center gap-4">
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      v.stage === "published" ? "bg-emerald-500/15 text-emerald-400" :
                                      v.stage === "ready" ? "bg-amber-500/15 text-amber-400" :
                                      "bg-white/10 text-gray-300"
                                    }`}>
                                      {v.stage}
                                    </span>
                                    <span className="text-gray-500 text-xs">
                                      {v.created_at ? new Date(v.created_at).toLocaleDateString() : "-"}
                                    </span>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {report.length === 0 && (
          <div className="text-center py-12">
            <User size={48} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No editors found</p>
          </div>
        )}
      </div>
    </div>
  );
}
